<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { Loader2 } from 'lucide-vue-next';
  import { firstQueryValue } from '@/shared/lib';
  import { TabsMenu } from '@/shared/ui';
  import type { TrainingDetailTrainee } from '@/entities/Training';
  import { useViewTraining } from '../model/useViewTraining';
  import { useCertificateUpload } from '../model/useCertificateUpload';
  import { useCollaboratorCertificateViewer } from '../model/useCollaboratorCertificateViewer';
  import { useCertificateGeneration } from '../model/useCertificateGeneration';
  import { useCertificateBatchGeneration } from '../model/useCertificateBatchGeneration';
  import { useCompleteTraining } from '../model/useCompleteTraining';
  import { useTrainingExport } from '../model/useTrainingExport';
  import { isTabbedTrainingType } from '../lib/isTabbedTrainingType';
  import { COLLABORATORS_TAB_ID } from '../lib/collaborators-list-query';
  import TrainingStatusBadge from './TrainingStatusBadge.vue';
  import TrainingViewActionsBar from './view/TrainingViewActionsBar.vue';
  import TrainingViewDetailsSections from './view/TrainingViewDetailsSections.vue';
  import TrainingCollaboratorsTab from './view/TrainingCollaboratorsTab.vue';

  const {
    loading,
    training,
    changeTraineeStatus,
    isChangingTraineeStatus,
    pageTitle,
    refresh,
    applyTraining,
  } = useViewTraining();

  const collaboratorsReloadToken = ref(0);

  const { requestCompletion } = useCompleteTraining({
    training: () => training.value,
    applyTraining,
    onCompleted: () => {
      collaboratorsReloadToken.value += 1;
    },
  });

  const { handleExportToExcel, isExporting } = useTrainingExport({
    trainingId: () => training.value?.id ?? null,
  });

  const { openCertificateUpload } = useCertificateUpload(() => {
    refresh();
  });

  const { openCertificate } = useCollaboratorCertificateViewer();

  const { generatingIds, openCertificateGeneration } = useCertificateGeneration(() => {
    refresh();
  });

  const { isGenerating: isGeneratingCertificates, requestBatchGeneration } =
    useCertificateBatchGeneration({
      trainingId: () => training.value?.id ?? null,
      onGenerated: () => {
        collaboratorsReloadToken.value += 1;
      },
    });

  const DETAILS_TAB = 'dados';
  const COLLABORATORS_TAB = COLLABORATORS_TAB_ID;

  const TAB_OPTIONS = [
    { id: DETAILS_TAB, text: 'Dados Treinamento' },
    { id: COLLABORATORS_TAB, text: 'Colaboradores' },
  ];

  const route = useRoute();
  const router = useRouter();

  const tabFromQuery = firstQueryValue(route.query.tab);
  const activeTab = ref(
    TAB_OPTIONS.some((option) => option.id === tabFromQuery) ? (tabFromQuery as string) : DETAILS_TAB
  );

  const hasTabs = computed(() =>
    training.value ? isTabbedTrainingType(training.value.type) : false
  );

  const effectiveTab = computed(() => (hasTabs.value ? activeTab.value : null));

  /**
   * `tab` is written by whoever owns the query at the moment. While the
   * collaborators tab is mounted it writes the whole query itself — term, sort,
   * page, quantity and filters, plus `tab` — so this page must not race it. Every
   * other state means the query has nothing left to say, and the absent key is the
   * default tab.
   */
  function clearQuery() {
    if (Object.keys(route.query).length === 0) return;
    router.replace({ path: route.path, query: {} });
  }

  watch(
    [() => training.value, effectiveTab],
    () => {
      if (!training.value) return;
      if (!hasTabs.value) activeTab.value = DETAILS_TAB;
      if (effectiveTab.value === COLLABORATORS_TAB) return;
      clearQuery();
    },
    { immediate: true }
  );

  function handleUploadCertificate(trainee: TrainingDetailTrainee): void {
    if (!training.value) return;

    openCertificateUpload(training.value.id, {
      memberTrainingId: trainee.id,
      hasCertificate: trainee.hasCertificate,
    });
  }

  function handleGenerateCertificate(trainee: TrainingDetailTrainee): void {
    if (!training.value) return;

    void openCertificateGeneration(training.value.id, {
      memberTrainingId: trainee.id,
      hasCertificate: trainee.hasCertificate,
    });
  }

  function handleViewCertificate(trainee: TrainingDetailTrainee): void {
    if (!training.value) return;

    void openCertificate(training.value.id, {
      memberTrainingId: trainee.id,
      certificateMimeType: trainee.certificateMimeType,
      certificateOriginalFileName: trainee.certificateOriginalFileName,
    });
  }
</script>

<template>
  <div
    data-testid="view-training-page"
    class="flex h-full flex-col gap-6 p-6"
  >
    <div
      v-if="loading"
      data-testid="view-training-loading"
      class="flex flex-1 items-center justify-center"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <template v-else-if="training">
      <div class="flex flex-col gap-6">
        <div class="flex flex-wrap items-center gap-4">
          <TrainingStatusBadge :status="training.status" />
          <h1
            data-testid="view-training-title"
            class="text-xl font-semibold text-gray-07"
          >
            {{ pageTitle }}
          </h1>
        </div>
        <TrainingViewActionsBar
          :status="training.status"
          :type="training.type"
          :completed-at="training.completedAt"
          :is-exporting="isExporting"
          :is-generating-certificates="isGeneratingCertificates"
          @complete="requestCompletion"
          @export="handleExportToExcel"
          @generate-certificates="requestBatchGeneration"
        />
      </div>

      <TabsMenu
        v-if="hasTabs"
        data-testid="view-training-tabs"
        :options="TAB_OPTIONS"
        :selected-option="activeTab"
        @option-selected="activeTab = $event"
      />

      <TrainingCollaboratorsTab
        v-if="hasTabs && activeTab === COLLABORATORS_TAB"
        :training-id="training.id"
        :reload-token="collaboratorsReloadToken"
      />

      <TrainingViewDetailsSections
        v-else
        :training="training"
        :show-trainees-section="!hasTabs"
        :is-changing-trainee-status="isChangingTraineeStatus"
        :generating-ids="[...generatingIds]"
        @change-trainee-status="changeTraineeStatus"
        @upload-certificate="handleUploadCertificate"
        @view-certificate="handleViewCertificate"
        @generate-certificate="handleGenerateCertificate"
      />
    </template>
  </div>
</template>
